import React, { useMemo } from 'react';
import { List, ListDataRefresher } from 'ui-builder';

import PlanTaskRow from './PlanTaskRow';

import styles from '../../../shared/widgets/TaskRowLayout.module.scss';
import SelectAllUpdater from '../../../shared/widgets/MassUpdate/SelectAllUpdater';
import TypeFilter from './Filter/TypeFilter';
import AssigneeFilter from './Filter/AssigneeFilter';
import RowRepeaterWithSort from './RowRepeaterWithSort';
import SmsEmailRecipientFilter from './Filter/SmsEmailRecipientFilter';
import CreateTasksButton from '../../view/CreateTasksButton';
import MassUpdateControls, { AdminMassUpdateControls } from '../../MassUpdate/MassUpdateControls';
import { DetailBoxesDatesWidgetPortalPlacer } from './DetailBoxesDatesWidget';
import { MassUpdateCheckbox } from 'features/tasks-list-feature';
import {
  Plan,
  PLAN_TASKS_UPDATED,
  PlanTask,
  replaceTagsIdsWithValues,
  TaskType,
  usePlanTasksService,
  useSmartTagsService
} from 'nekst-api';
import { PlanPermission, useAuthorizationChecker } from 'authorization-scope';
import {
  EditPlanButton,
  PlanProjectTypeSetter,
  PlanTasksClientSideFilter, useProjectTypeHelper
} from 'features/tasks-forms';
import { useNavigate } from 'react-router-dom';
import DefaultPlanHelper from '../../view/DefaultPlanHelper';
import { OrderedListWrapper } from 'features/nekst-widgets';
import InnerBlock from '../../../shared/web/layout/InnerBlock';
import { AdminPlanButtons } from './Admin/AdminPlanButtons';
import FixHierarchyButton from '../../view/FixHierarchyButton';

export interface PlanTasksListProps {
  plan: Plan,
  detailsFieldsSetName?: string;
  setupAreaId?: number;
  setupAreaVersion?: string;
}

export default function PlanTasksList(props: PlanTasksListProps) {
  const planTasksService = usePlanTasksService();
  const projectTypeHelper = useProjectTypeHelper();

  const { isGranted } = useAuthorizationChecker();

  const smartTagsService = useSmartTagsService();

  const navigate = useNavigate();

  const getDataFunc = async () => {
    const tags = await smartTagsService.getSmartTagsByPlanId(
      props.plan.id,
      TaskType.EMAIL,
      undefined
    );

    const tasks = await planTasksService.getPlanTasks(props.plan.id);
    return tasks
      .map((singleTask) => {
        return {
          ...singleTask,
          _renderedName: singleTask.type === TaskType.EMAIL ? replaceTagsIdsWithValues(singleTask.name, tags) : singleTask.name
        };
      })
      .sort(planTasksService.getTasksCompareFunc());
  };

  const titleLine2 = useMemo(() => {
    if (props.plan.projectType) {
      let result = projectTypeHelper.getLabel(props.plan.projectType);

      if (props.detailsFieldsSetName) {
        result += ` - ${props.detailsFieldsSetName}`;
      }

      const setupAreaName = props.plan._setupArea?.name;

      if (setupAreaName !== 'Internal') {
        result += ` - ${setupAreaName}`;
      }

      return result;
    } else {
      return undefined;
    }
  }, [props.plan.projectType, props.detailsFieldsSetName]);

  const editButton = props.setupAreaId && props.setupAreaVersion ? (
    <AdminPlanButtons
      plan={props.plan}
      setupAreaId={props.setupAreaId}
      setupAreaVersion={props.setupAreaVersion}
    />
  ) : (<EditPlanButton
    key="editplan"
    plan={props.plan}
    goToPlansListFunc={() => {
      if (props.setupAreaId && props.setupAreaVersion) {
        navigate('/admin/setupareas/' + props.setupAreaId + '/' + props.setupAreaVersion + '/workflows');
      } else {
        navigate('/plans');
      }
    }}
    goToPlanFunc={(plan) => {
      if (props.setupAreaId && props.setupAreaVersion) {
        navigate(`/admin/setupareas/${props.setupAreaId}/${props.setupAreaVersion}/workflows/${plan.id}`);
      } else {
        navigate(`/plans/${plan.id}`);
      }
    }}
  />);

  return (
    <List
      getDataFunc={getDataFunc}
      dependencies={[props.plan.id]}
    >
      <InnerBlock
        title={props.plan.name}
        titleLine2={titleLine2}
        headerRight={[
          <MassUpdateCheckbox key="massupdate" />,
          <CreateTasksButton key="createtasks" id={props.plan.id} />,
          editButton
        ]}
      >
        {props.setupAreaId && props.setupAreaVersion ? <AdminMassUpdateControls /> : <MassUpdateControls />}
        <DefaultPlanHelper />
        <PlanProjectTypeSetter />
        <FixHierarchyButton planId={props.plan.id} />
        <PlanTasksClientSideFilter>
          <TypeFilter />
          <AssigneeFilter />
          <SmsEmailRecipientFilter />
          <DetailBoxesDatesWidgetPortalPlacer />
          <ListDataRefresher eventName={PLAN_TASKS_UPDATED} />
          <SelectAllUpdater />
          <OrderedListWrapper
            orderingConfig={{
              getOrderGroupFunc: (item: PlanTask) => {
                let groupName;
                if (item.dueDateConfig) {
                  groupName = `dueDate-${item.dueDateConfig?.days?.toString()}-${item.dueDateConfig?.when}`;
                } else {
                  groupName = `parentTask-${item.parentTaskRelation?.days}-${item.parentTaskRelation?.parentTaskId}`;
                }
                return groupName;
              },
              updateOrderFunc: planTasksService.setOrder,
              isEnabled: isGranted(PlanPermission.UPDATE)
            }}
          >
            <RowRepeaterWithSort
              idField="id"
              blockClassName={styles.block}
              groupClassName={styles.group}
              parentIdField="parentTaskRelation.parentTaskId"
              noDataMessage="No tasks matching current filter criteria"
            >
              <PlanTaskRow />
            </RowRepeaterWithSort>
          </OrderedListWrapper>
        </PlanTasksClientSideFilter>
      </InnerBlock>
    </List>
  );
}
